import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import TableCell from "../../components/common/ui/TableCell";
import ApprovalActionDoc from "../../components/approval/ApprovalAction_doc";
import SignTableDoc from "../../components/approval/SignTable_doc";
import PdfPreview from "../common/viewer/PdfPreview";
import { getDocSignatureInfo } from "../../api/papers";

export default function MaterialPSView({ data, meta }) {
  const { id: projectId, postId } = useParams();
  const navigate = useNavigate();
  const [creatorSignature, setCreatorSignature] = useState(null);

  // ---------------------------------------
  // 기본 데이터 정리
  // ---------------------------------------
  const dns = data?.dns || data?.doc_dns || postId || "";
  const header = data?.header || {}; // ⭐ material은 header가 루트
  const formTypeRaw = data?.doc_type || data?.form_type || "";
  const formType = String(formTypeRaw).toUpperCase();
  const status = data?.status || "";

  // 작성자 정보
  const createdBy = data?.created_by || {};
  const createdByName = createdBy?.name || data?.created_by_name || "-";

  // 작성자 서명 정보 가져오기
  useEffect(() => {
    const loadSignature = async () => {
      try {
        const sig = await getDocSignatureInfo(dns);
        console.log("📝 MaterialPSView - 서명 정보:", sig.data);
        setCreatorSignature(sig.data?.sign || null);
      } catch (err) {
        console.error("서명 정보 로드 실패:", err);
        setCreatorSignature(null);
      }
    };

    if (dns) {
      loadSignature();
    }
  }, [dns]);

  // 디버깅: 작성자 정보 확인
  console.log("📝 MaterialPSView - 작성자 정보:", {
    createdBy,
    createdByName,
    data_created_by: data?.created_by,
  });

  // 반려 상태인지 확인
  const isRejected = status === "rejected";

  // 수정 버튼 클릭 핸들러
  const handleEdit = () => {
    navigate(`/ project / ${projectId} /material/plan / ${dns} `);
  };

  // ---------------------------------------
  // 날짜 포맷
  // ---------------------------------------
  const fmtDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}.${month}.${day} `;
  };

  // ---------------------------------------
  // 공종 Division 체크박스
  // ---------------------------------------
  const divisionOptions = [
    { key: 'arch', label: '건축 Arch', checked: header.division === 'arch' || header.division === '건축' },
    { key: 'structural', label: '구조 Structural', checked: header.division === 'structural' || header.division === '구조' },
    { key: 'civil', label: '토목 Civil', checked: header.division === 'civil' || header.division === '토목' },
    { key: 'mechanical', label: '설비 Mechanical', checked: header.division === 'mechanical' || header.division === '설비' },
    { key: 'electrical', label: '전기 Electrical', checked: header.division === 'electrical' || header.division === '전기' },
    { key: 'firefighting', label: '소방 Firefighting', checked: header.division === 'firefighting' || header.division === '소방' },
    { key: 'interior', label: '의장 Interior', checked: header.division === 'interior' || header.division === '의장' },
    { key: 'other', label: '기타 Other', checked: header.division === 'other' || header.division === '기타' },
  ];

  // ---------------------------------------
  // 종류 Type 체크박스
  // ---------------------------------------
  const typeOptions = [
    { key: 'SD', label: '도면 SD' },
    { key: 'SC', label: '공정표 SC' },
    { key: 'MT', label: '자재 MT', checked: true }, // PS는 항상 자재
    { key: 'DT', label: '자료 DT' },
    { key: 'SA', label: '샘플 SA' },
    { key: 'CA', label: '계산서 CA' },
    { key: 'O', label: '기타 O' },
  ];

  // ---------------------------------------
  // 결과 Action 체크박스
  // ---------------------------------------
  const actionOptions = [
    { key: 'A', label: 'A : Approved ; proceed as submitted 승인, 제출한 내용 대로 진행' },
    { key: 'B', label: 'B : Proceed subject to conformance with notes 注 내용대로 수정 후 진행' },
    { key: 'C', label: 'C : Revised & Re-submit; Work may not proceed 수정 후 재요청, 진행불가' },
    { key: 'D', label: 'D : Rejected 승인 불가' },
  ];

  const selectedAction = header.action || '';

  // ---------------------------------------
  // 안전장치
  // ---------------------------------------
  if (formType !== "PS") {
    return (
      <div className="p-6 text-sm text-red-600">
        잘못된 뷰 매칭입니다. (현재 문서 타입: {formType})
      </div>
    );
  }

  // ---------------------------------------
  // 렌더링
  // ---------------------------------------
  return (
    <div className="space-y-6">
      {/* ============================================
          자재승인 요청 정보 섹션
      ============================================ */}
      <div className="text-lg font-bold mb-4">
        자재승인 요청 정보
      </div>

      <table className="w-full border-collapse text-body">
        <tbody>
          {/* 관련문서 / 관련도면/시방서 */}
          <tr>
            <td className="tbl-label-bg w-32">
              관련문서
            </td>
            <td className="tbl-value-bg" colSpan={5}>
              <input
                type="text"
                className="w-full px-2 py-1 border-none bg-transparent"
                value={header.related_document || ""}
                readOnly
                placeholder="-"
              />
            </td>
            <td className="tbl-label-bg w-32">
              관련도면/시방서
            </td>
            <td className="tbl-value-bg" colSpan={5}>
              {header.relatedDrawing?.name ? (
                <a
                  href={header.relatedDrawing.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  {header.relatedDrawing.name}
                </a>
              ) : (
                "-"
              )}
            </td>
          </tr>

          {/* 공종 - 선택된 값만 표시 */}
          <tr>
            <td className="tbl-label-bg w-32">
              공종
            </td>
            <td className="tbl-value-bg" colSpan={11}>
              {divisionOptions.find(opt => opt.checked)?.label?.split(' ')[0] || header.division || "-"}
            </td>
          </tr>

          {/* 종류 - PS(자재) 고정 */}
          <tr>
            <td className="tbl-label-bg">
              종류
            </td>
            <td className="tbl-value-bg" colSpan={11}>
              자재
            </td>
          </tr>

          {/* 위치 - 1줄로 통합 */}
          <tr>
            <td className="tbl-label-bg">
              위치
            </td>
            <td className="tbl-value-bg" colSpan={11}>
              <div className="flex items-center">
                <div className="flex-1 px-3 border-r border-gray-400">
                  <span>지역 : </span>
                  <span className="font-medium">{header.area || ""}</span>
                </div>
                <div className="flex-1 px-3 border-r border-gray-400">
                  <span>층 : </span>
                  <span className="font-medium">{header.level || ""}</span>
                </div>
                <div className="flex-1 px-3">
                  <span>좌표 : </span>
                  <span className="font-medium">{header.grid || ""}</span>
                </div>
              </div>
            </td>
          </tr>

          {/* 승인요청일 / 시행일자 */}
          <tr>
            <td className="tbl-label-bg">
              승인요청일
            </td>
            <td className="tbl-value-bg" colSpan={5}>
              {fmtDate(header.request_date) || "20  .  ."}
            </td>
            <td className="tbl-label-bg">
              시행일자
            </td>
            <td className="tbl-value-bg" colSpan={5}>
              {fmtDate(header.action_date) || "20  .  ."}
            </td>
          </tr>

          {/* 내용 Description */}
          <tr>
            <td className="tbl-label-bg align-top">
              내용
            </td>
            <td className="tbl-value-bg" colSpan={11}>
              <div className="min-h-[60px] whitespace-pre-wrap">
                {header.description || "-"}
              </div>
            </td>
          </tr>

          {/* ============================================
              현장대리인 / 일자
          ============================================ */}
          <tr>
            <td className="tbl-label-bg">
              현장대리인
            </td>
            <td className="tbl-value-bg text-center" colSpan={5}>
              {creatorSignature ? (
                <div className="flex flex-col items-center justify-center py-2">
                  <img
                    src={creatorSignature}
                    alt={`${createdByName} 서명`}
                    className="max-h-16 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span style={{ display: 'none' }} className="text-gray-500">
                    {createdByName}
                  </span>
                </div>
              ) : (
                <div className="h-16 flex items-center justify-center text-gray-500">
                  {createdByName}
                </div>
              )}
            </td>
            <td className="tbl-label-bg">
              일자
            </td>
            <td className="tbl-value-bg" colSpan={5}>
              {fmtDate(data.created_at) || "20  .  ."}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ---------------------------------------
          PDF 미리보기
      --------------------------------------- */}
      {header.relatedDrawing?.url && (
        <div className="border rounded-lg bg-gray-50 p-4 mt-6">
          <div className="text-body-bold mb-2">관련 도면 미리보기</div>
          <PdfPreview fileUrl={header.relatedDrawing.url} />
        </div>
      )}

      {/* ---------------------------------------
          결재 / 서명
      --------------------------------------- */}
      <div className="mt-8">
        <ApprovalActionDoc dns={dns} />
      </div>

      <SignTableDoc dns={dns} />
    </div>
  );
}
